/* Phase 452 — Shared searchable Bank Account picker.
   Used by Sales (Customer payments), Finance (Supplier payments), and
   anywhere else a user needs to pick from Finance → Bank Accounts.
   Shows an empty state with a "Create bank account" shortcut when no
   accounts exist yet. */
import React, { useState } from 'react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Check, CaretUpDown, Plus } from '@phosphor-icons/react';

export function BankAccountCombobox({ t, bankAccounts, value, onChange, onCreateBank, testid = 'bank-combobox', placeholder }) {
  const [open, setOpen] = useState(false);
  const accounts = bankAccounts || [];
  const selected = accounts.find(b => b.account_id === value);
  const label = (b) => b?.name || b?.account_name || b?.bank_name || '';
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="rounded-none mt-1 w-full justify-between font-normal"
          data-testid={`${testid}-trigger`}
        >
          <span className={selected ? '' : 'text-zinc-400'}>
            {selected ? label(selected) : (placeholder || (t && t('selectBankAccount')) || 'Select bank account…')}
          </span>
          <CaretUpDown size={14} className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0 rounded-none" align="start" data-testid={`${testid}-popover`}>
        {accounts.length === 0 ? (
          <div className="p-4 text-center space-y-3" data-testid={`${testid}-empty`}>
            <p className="text-sm text-zinc-600">{(t && t('noBankAccountsCreated')) || 'No bank accounts created'}</p>
            {onCreateBank && (
              <Button
                type="button"
                size="sm"
                className="rounded-none bg-emerald-700 hover:bg-emerald-800 w-full"
                onClick={() => { setOpen(false); onCreateBank(); }}
                data-testid={`${testid}-create-shortcut`}
              >
                <Plus size={14} className="mr-1" /> {(t && t('createBankAccount')) || 'Create bank account'}
              </Button>
            )}
          </div>
        ) : (
          <Command>
            <CommandInput placeholder={(t && t('searchBankAccount')) || 'Search bank account…'} data-testid={`${testid}-search`} />
            <CommandList>
              <CommandEmpty>{(t && t('noMatches')) || 'No matches.'}</CommandEmpty>
              <CommandGroup>
                {accounts.map(b => (
                  <CommandItem
                    key={b.account_id}
                    value={label(b)}
                    onSelect={() => { onChange?.(b.account_id); setOpen(false); }}
                    data-testid={`${testid}-option-${b.account_id}`}
                  >
                    <Check size={14} className={`mr-2 ${value === b.account_id ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="flex-1">{label(b)}</span>
                    {b.iban && <span className="text-[10px] text-zinc-400 font-mono ml-2">{b.iban.slice(-6)}</span>}
                  </CommandItem>
                ))}
                {onCreateBank && (
                  <CommandItem
                    value="__create"
                    onSelect={() => { setOpen(false); onCreateBank(); }}
                    className="border-t border-zinc-100 mt-1 pt-2 text-emerald-700"
                    data-testid={`${testid}-create-inline`}
                  >
                    <Plus size={14} className="mr-2" />
                    {(t && t('createBankAccount')) || 'Create bank account'}
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default BankAccountCombobox;
